import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Play, ArrowLeft, Sparkle, Sun, Shuffle, MoonStars, Hourglass,
    BookOpen, MusicNotes, Briefcase, Heart, AirplaneTakeoff, X,
} from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

// Theme slug → icon (matches /api/session-themes).
const THEME_ICON = {
    "early-years": BookOpen,
    "love-and-home": Heart,
    "work-and-service": Briefcase,
    "things-they-loved": Sparkle,
    "music-and-culture": MusicNotes,
    "a-bigger-life": AirplaneTakeoff,
};

// The 5 alternate session modes, in order of expected daily use.
const MODE_CARDS = [
    { slug: "today",     label: "Today's session", sub: "A fresh handful — different each day", icon: Sun,        accent: "#F4D068", subColor: "#8A6A0E" },
    { slug: "themed",    label: "Choose a theme",  sub: "Wander into a chapter of life",         icon: Sparkle,    accent: "#8C9ECB", subColor: "#4A5A95" },
    { slug: "era",       label: "Travel a decade", sub: "The '60s, the '70s — pick a year",     icon: Hourglass,  accent: "#A99070", subColor: "#7A5F40" },
    { slug: "shuffle",   label: "Shuffle",         sub: "All memories, fresh order each time",   icon: Shuffle,    accent: "#9CACD6", subColor: "#4A5A95" },
    { slug: "lights-on", label: "Lights On",       sub: "Calm evening session — gentle pace",    icon: MoonStars,  accent: "#7A88B8", subColor: "#4A5A95" },
];

export default function ReminisceStart() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [memCount, setMemCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [themes, setThemes] = useState([]);
    const [eras, setEras] = useState([]);
    const [picker, setPicker] = useState(null); // "themed" | "era" | null

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [{ data }, themesRes, erasRes] = await Promise.all([
                    api.get(`/profiles/${id}/playlist`),
                    api.get(`/session-themes`).catch(() => ({ data: { themes: [] } })),
                    api.get(`/profiles/${id}/eras`).catch(() => ({ data: { eras: [] } })),
                ]);
                if (!active) return;
                setProfile(data.profile);
                setMemCount((data.playlist || []).length);
                setThemes(themesRes.data.themes || []);
                setEras(erasRes.data.eras || []);
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load.");
                navigate("/dashboard");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [id, navigate]);

    if (loading || !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="reminisce-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    const displayName = profile.is_couple
        ? `${profile.subject_name} & ${profile.subject_name_2}`
        : profile.subject_name;

    const startMode = (mode, extra) => {
        const q = new URLSearchParams();
        if (mode && mode !== "full") q.set("mode", mode);
        if (extra?.theme) q.set("theme", extra.theme);
        if (extra?.era)   q.set("era", extra.era);
        const suffix = q.toString() ? `?${q.toString()}` : "";
        navigate(`/profiles/${id}/reminisce/play${suffix}`);
    };

    const onModeClick = (slug) => {
        if (slug === "themed") return setPicker("themed");
        if (slug === "era")    return setPicker("era");
        startMode(slug);
    };

    return (
        <div className="min-h-screen rem-watercolor-bg flex flex-col" data-testid="reminisce-start">
            <Link
                to={`/profiles/${id}`}
                className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-xs text-[#8A8786] hover:text-[#6B7DB8] transition-colors"
                data-testid="reminisce-exit"
            >
                <ArrowLeft size={14} /> Carer · back
            </Link>

            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-4xl text-center"
                >
                    <div className="flex justify-center mb-8">
                        <ForgetMeNotIcon size={88} className="animate-float-gentle" />
                    </div>
                    <p className="font-serif text-3xl sm:text-4xl text-[#2D2B2A] mb-2">Good morning,</p>
                    <h1
                        className="font-serif text-5xl sm:text-7xl text-[#4A5A95] italic leading-tight"
                        data-testid="reminisce-greeting"
                    >
                        {displayName},
                        <br/>
                        <span className="text-[#2D2B2A]">let&apos;s </span>reminisce
                    </h1>

                    <p className="mt-10 text-[#8A8786] text-base sm:text-lg">
                        {memCount} {memCount === 1 ? "memory" : "memories"} · gathered with love by your family
                    </p>

                    {/* Primary CTA */}
                    <div className="mt-12 max-w-md mx-auto">
                        <button
                            onClick={() => startMode("full")}
                            className="rem-start-button group w-full inline-flex items-center justify-center gap-3 px-8 py-6 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-xl sm:text-2xl transition-all shadow-rem-glow disabled:opacity-50"
                            data-testid="reminisce-play-btn"
                            disabled={memCount === 0}
                        >
                            <Play size={28} weight="fill" />
                            Play my story
                        </button>
                    </div>

                    {/* Alternative modes */}
                    {memCount > 0 && (
                        <div className="mt-14" data-testid="reminisce-modes">
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-6">
                                Or choose a different way to remember
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                {MODE_CARDS.map((m) => {
                                    const Icon = m.icon;
                                    const disabled = m.slug === "era" && eras.length === 0;
                                    return (
                                        <button
                                            key={m.slug}
                                            onClick={() => onModeClick(m.slug)}
                                            disabled={disabled}
                                            className="group bg-white border-2 rounded-2xl p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-rem-card disabled:opacity-40 disabled:hover:translate-y-0"
                                            style={{ borderColor: `${m.accent}55` }}
                                            data-testid={`reminisce-mode-${m.slug}`}
                                        >
                                            <span
                                                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-105"
                                                style={{ background: `${m.accent}22` }}
                                            >
                                                <Icon size={20} weight="fill" color={m.subColor} />
                                            </span>
                                            <p className="font-serif text-lg text-[#2D2B2A] leading-tight">{m.label}</p>
                                            <p className="mt-1.5 text-[11px] text-[#8A8786] leading-snug">{m.sub}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {memCount === 0 && (
                        <p className="mt-8 text-sm text-[#8A8786]" data-testid="reminisce-empty">
                            No memories have been added to this remembr yet.{" "}
                            <Link to={`/profiles/${id}`} className="text-[#6B7DB8] underline">
                                Begin gathering them
                            </Link>
                        </p>
                    )}
                </motion.div>
            </div>

            <p className="text-center pb-8 text-xs text-[#8A8786] tracking-[0.25em] uppercase">
                remembr · forget me not
            </p>

            <PickerOverlay
                kind={picker}
                themes={themes}
                eras={eras}
                onClose={() => setPicker(null)}
                onPickTheme={(slug) => { setPicker(null); startMode("themed", { theme: slug }); }}
                onPickEra={(slug)   => { setPicker(null); startMode("era",    { era:   slug }); }}
            />
        </div>
    );
}

// Reused overlay: serves both theme picker and era picker via `kind`.
function PickerOverlay({ kind, themes, eras, onClose, onPickTheme, onPickEra }) {
    const open = kind === "themed" || kind === "era";
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40 bg-[#1a1c25]/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                    data-testid={`${kind}-picker-overlay`}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="relative w-full max-w-3xl bg-[#FDFBF7] rounded-3xl p-8 sm:p-10 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#F4F6FB] hover:bg-[#E5E1D8] text-[#5C5A59] flex items-center justify-center transition-colors"
                            data-testid={`${kind}-picker-close`}
                            aria-label="Close picker"
                        >
                            <X size={20} weight="bold" />
                        </button>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-3 text-center">
                            {kind === "themed" ? "Themed session" : "Travel to a decade"}
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl text-center text-[#2D2B2A] leading-tight">
                            {kind === "themed" ? (
                                <>What part of the story would you like<br/><em className="italic text-[#4A5A95]">to wander into today?</em></>
                            ) : (
                                <>Which decade would you like<br/><em className="italic text-[#4A5A95]">to step back into?</em></>
                            )}
                        </h2>

                        {kind === "themed" && (
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {themes.map((t) => {
                                    const Icon = THEME_ICON[t.slug] || Sparkle;
                                    return (
                                        <button
                                            key={t.slug}
                                            onClick={() => onPickTheme(t.slug)}
                                            className="group flex items-center gap-4 p-5 rounded-2xl border border-[#E5E1D8] bg-white hover:border-[#6B7DB8] hover:bg-[#F4F6FB] text-left transition-all hover:-translate-y-0.5 shadow-rem-soft"
                                            data-testid={`theme-card-${t.slug}`}
                                        >
                                            <span className="w-12 h-12 rounded-2xl bg-[#F4F6FB] group-hover:bg-white flex items-center justify-center shrink-0 transition-colors">
                                                <Icon size={24} weight="light" color="#4A5A95" />
                                            </span>
                                            <span className="font-serif text-xl text-[#2D2B2A] group-hover:text-[#4A5A95] transition-colors">
                                                {t.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {kind === "era" && (
                            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {eras.length === 0 ? (
                                    <p className="col-span-full text-center text-sm text-[#8A8786] py-8">
                                        No dated memories yet. Add a year to a few memories to unlock decade travel.
                                    </p>
                                ) : eras.map((e) => (
                                    <button
                                        key={e.slug}
                                        onClick={() => onPickEra(e.slug)}
                                        className="group flex flex-col items-start gap-1 p-5 rounded-2xl border border-[#E5E1D8] bg-white hover:border-[#6B7DB8] hover:bg-[#F4F6FB] text-left transition-all hover:-translate-y-0.5 shadow-rem-soft"
                                        data-testid={`era-card-${e.slug}`}
                                    >
                                        <p className="font-serif text-2xl text-[#2D2B2A] group-hover:text-[#4A5A95] transition-colors leading-none">
                                            {e.label}
                                        </p>
                                        <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A8786] mt-2">
                                            {e.count} {e.count === 1 ? "memory" : "memories"}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        )}

                        <p className="mt-6 text-center text-xs text-[#8A8786]">
                            Every session opens with the welcome scene.
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
